import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Pagination,
  FormHelperText,
  Avatar,
} from '@mui/material';
import { 
  Check, 
  Close, 
  EventAvailable, 
  Warning,
  Refresh,
  FilterList,
  Download,
  Visibility,
  HolidayVillage,
  People,
  Verified,
  Cancel
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { holidaysAPI, employeesAPI } from '../services/api';

// Helper function to convert data to CSV
const convertToCSV = (data) => {
  if (!data || data.length === 0) return '';
  
  const headers = ['Employee Name', 'Employee ID', 'Email', 'Department', 'Holiday Name', 'Holiday Date', 'Status', 'Requested Date', 'Rejection Reason'];
  const rows = data.map(item => [
    `${item.employee?.first_name || ''} ${item.employee?.last_name || ''}`,
    item.employee?.employee_id || '',
    item.employee?.email || '',
    item.employee?.department || '',
    item.holiday?.holiday_name || '',
    item.holiday?.holiday_date || '',
    item.status || '',
    item.requested_date ? new Date(item.requested_date).toLocaleDateString() : '',
    item.rejection_reason || ''
  ]);
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');
  
  return csvContent;
};

const HolidayApprovals = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selections, setSelections] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState('pending');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [departments, setDepartments] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [rejectDialog, setRejectDialog] = useState({ open: false, ids: [], reason: '' });
  const [viewDialog, setViewDialog] = useState({ open: false, selection: null });
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    total: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Load departments on mount
  useEffect(() => {
    loadDepartments();
  }, []);

  // Load selections when filters change
  useEffect(() => {
    loadSelections();
  }, [year, statusFilter, departmentFilter, pagination.page]);

  const loadDepartments = async () => {
    try {
      const response = await holidaysAPI.getDepartments();
      if (response.data.success) {
        setDepartments(response.data.data);
      }
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadSelections = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      
      const params = {
        year,
        status: statusFilter || undefined,
        department: departmentFilter || undefined,
        page: pagination.page,
        limit: pagination.limit
      };
      
      const response = await holidaysAPI.getAllSelections(params);
      
      if (response.data.success) {
        setSelections(response.data.data.selections || response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.total || response.data.data.length,
          totalPages: response.data.data.totalPages || Math.ceil((response.data.data.total || response.data.data.length) / prev.limit)
        }));
        if (response.data.data.statusCounts) {
          setStatusCounts(response.data.data.statusCounts);
        }
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to load holiday selections' });
      }
    } catch (error) {
      console.error('Error loading selections:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to load holiday selections' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const pendingIds = selections.filter(s => s.status === 'pending').map(s => s.id);
      setSelectedIds(pendingIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelect = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleApprove = async (ids) => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const idsArray = Array.isArray(ids) ? ids : [ids];
      
      if (idsArray.length === 0) {
        setMessage({ type: 'warning', text: 'No items selected' });
        return;
      }

      let response;
      if (idsArray.length === 1) {
        response = await holidaysAPI.approveSelection(idsArray[0]);
      } else {
        response = await holidaysAPI.bulkUpdateSelections({
          selectionIds: idsArray,
          status: 'approved'
        });
      }

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: idsArray.length === 1 ? 'Holiday approved successfully' : `${idsArray.length} holiday(s) approved successfully` 
        });
        loadSelections();
        setSelectedIds([]);
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to approve holidays' });
      }
    } catch (error) {
      console.error('Error approving:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to approve holidays' });
    } finally {
      setSaving(false);
    }
  };

  const openRejectDialog = (ids) => {
    setRejectDialog({ 
      open: true, 
      ids: Array.isArray(ids) ? ids : [ids], 
      reason: '' 
    });
  };

  const handleReject = async () => {
    if (rejectDialog.ids.length === 0) {
      setMessage({ type: 'warning', text: 'No items selected' });
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      let response;
      if (rejectDialog.ids.length === 1) {
        response = await holidaysAPI.rejectSelection(rejectDialog.ids[0], {
          rejection_reason: rejectDialog.reason
        });
      } else {
        response = await holidaysAPI.bulkUpdateSelections({
          selectionIds: rejectDialog.ids,
          status: 'rejected',
          rejection_reason: rejectDialog.reason
        });
      }

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: rejectDialog.ids.length === 1 ? 'Holiday rejected successfully' : `${rejectDialog.ids.length} holiday(s) rejected successfully` 
        });
        loadSelections();
        setSelectedIds([]);
        setRejectDialog({ open: false, ids: [], reason: '' });
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to reject holidays' });
      }
    } catch (error) {
      console.error('Error rejecting:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to reject holidays' });
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetails = (selection) => {
    setViewDialog({ open: true, selection });
  };

  const handleRefresh = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadSelections();
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  const handleExport = async () => {
    try {
      const params = {
        year,
        status: statusFilter || undefined,
        department: departmentFilter || undefined,
        format: 'csv'
      };
      
      const response = await holidaysAPI.getAllSelections(params);
      if (!response.data.success) {
        throw new Error('Failed to fetch data for export');
      }
      const selections = response.data.data.selections || response.data.data;
      const csvContent = convertToCSV(selections);
      
      const url = window.URL.createObjectURL(new Blob([csvContent], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `holiday_selections_${year}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: 'Export completed successfully' });
    } catch (error) {
      console.error('Error exporting:', error);
      setMessage({ type: 'error', text: 'Failed to export data' });
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'approved':
        return (
          <Chip 
            icon={<Check />} 
            label="Approved" 
            size="small" 
            sx={{ 
              bgcolor: 'rgba(76, 175, 80, 0.2)', 
              color: '#81C784',
              border: '1px solid #81C784'
            }} 
          />
        );
      case 'pending':
        return (
          <Chip 
            icon={<EventAvailable />} 
            label="Pending" 
            size="small" 
            sx={{ 
              bgcolor: 'rgba(255, 152, 0, 0.2)', 
              color: '#FFB74D',
              border: '1px solid #FFB74D'
            }} 
          />
        );
      case 'rejected':
        return (
          <Chip 
            icon={<Close />} 
            label="Rejected" 
            size="small" 
            sx={{ 
              bgcolor: 'rgba(244, 67, 54, 0.2)', 
              color: '#EF9A9A',
              border: '1px solid #EF9A9A'
            }} 
          />
        );
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const getHolidayTypeColor = (type) => {
    switch (type) {
      case 'public':
        return { bgcolor: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37', border: '1px solid #D4AF37' };
      case 'company':
        return { bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#81C784', border: '1px solid #81C784' };
      case 'optional':
        return { bgcolor: 'rgba(33, 150, 243, 0.2)', color: '#90CAF9', border: '1px solid #90CAF9' };
      default:
        return { bgcolor: 'rgba(100, 100, 100, 0.2)', color: '#B4C4A8', border: '1px solid #B4C4A8' };
    }
  };

  const pendingCount = statusCounts.pending;
  const approvedCount = statusCounts.approved;
  const rejectedCount = statusCounts.rejected;
  const totalCount = statusCounts.total;
  const selectedPendingCount = selectedIds.length;

  return (
    <Box sx={{ bgcolor: '#0A1929', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#D4AF37' }}>
            <HolidayVillage sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle' }} />
            Holiday Approvals
          </Typography>
          <Tooltip title="Refresh">
            <IconButton 
              onClick={handleRefresh} 
              disabled={loading}
              sx={{ color: '#D4AF37', '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {message.text && (
          <Alert 
            severity={message.type} 
            sx={{ 
              mb: 2, 
              bgcolor: 'rgb(10, 25, 41)', 
              color: message.type === 'success' ? '#81C784' : message.type === 'error' ? '#EF9A9A' : '#FFB74D',
              border: `1px solid ${message.type === 'success' ? '#81C784' : message.type === 'error' ? '#EF9A9A' : '#FFB74D'}`,
              '& .MuiAlert-icon': { color: message.type === 'success' ? '#81C784' : message.type === 'error' ? '#EF9A9A' : '#FFB74D' }
            }} 
            onClose={() => setMessage({ type: '', text: '' })}
          >
            {message.text}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: 'rgb(10, 25, 41)', 
              border: '1px solid #D4AF37',
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255, 152, 0, 0.2)', 
                  width: 56, 
                  height: 56, 
                  mx: 'auto', 
                  mb: 1,
                  color: '#FFB74D'
                }}>
                  <EventAvailable sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h3" sx={{ color: '#FFB74D', fontWeight: 700 }}>
                  {pendingCount}
                </Typography>
                <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                  Pending Approval
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: 'rgb(10, 25, 41)', 
              border: '1px solid #D4AF37',
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(76, 175, 80, 0.2)', 
                  width: 56, 
                  height: 56, 
                  mx: 'auto', 
                  mb: 1,
                  color: '#81C784'
                }}>
                  <Verified sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h3" sx={{ color: '#81C784', fontWeight: 700 }}>
                  {approvedCount}
                </Typography>
                <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                  Approved
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: 'rgb(10, 25, 41)', 
              border: '1px solid #D4AF37',
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(244, 67, 54, 0.2)', 
                  width: 56, 
                  height: 56, 
                  mx: 'auto', 
                  mb: 1,
                  color: '#EF9A9A'
                }}>
                  <Cancel sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h3" sx={{ color: '#EF9A9A', fontWeight: 700 }}>
                  {rejectedCount}
                </Typography>
                <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                  Rejected
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: 'rgb(10, 25, 41)', 
              border: '1px solid #D4AF37',
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(212, 175, 55, 0.2)', 
                  width: 56, 
                  height: 56, 
                  mx: 'auto', 
                  mb: 1,
                  color: '#D4AF37'
                }}>
                  <People sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h3" sx={{ color: '#D4AF37', fontWeight: 700 }}>
                  {totalCount}
                </Typography>
                <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                  Total Records
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ 
          p: 2, 
          mb: 3, 
          bgcolor: 'rgb(10, 25, 41)', 
          border: '1px solid #D4AF37',
          borderRadius: 3 
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#B4C4A8' }}>Year</InputLabel>
                <Select
                  value={year}
                  label="Year"
                  onChange={(e) => {
                    setYear(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  sx={{ 
                    color: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C19B2E' }
                  }}
                >
                  {[2022, 2023, 2024, 2025, 2026].map(y => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#B4C4A8' }}>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  sx={{ 
                    color: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C19B2E' }
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#B4C4A8' }}>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  label="Department"
                  onChange={(e) => {
                    setDepartmentFilter(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  sx={{ 
                    color: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C19B2E' }
                  }}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12} md={3}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleExport}
                  disabled={loading || selections.length === 0}
                  sx={{ borderColor: '#D4AF37', color: '#D4AF37', '&:hover': { borderColor: '#C19B2E', bgcolor: 'rgba(212,175,55,0.1)' } }}
                >
                  Export
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{ borderColor: '#D4AF37', color: '#D4AF37', '&:hover': { borderColor: '#C19B2E', bgcolor: 'rgba(212,175,55,0.1)' } }}
                >
                  Filter
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Bulk Actions Alert */}
        {selectedPendingCount > 0 && statusFilter === 'pending' && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2, 
              bgcolor: 'rgb(10, 25, 41)', 
              color: '#90CAF9',
              border: '1px solid #90CAF9',
              '& .MuiAlert-icon': { color: '#90CAF9' }
            }}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  color="success" 
                  size="small" 
                  onClick={() => handleApprove(selectedIds)}
                  disabled={saving}
                  sx={{ color: '#81C784' }}
                >
                  Approve ({selectedPendingCount})
                </Button>
                <Button 
                  color="error" 
                  size="small" 
                  onClick={() => openRejectDialog(selectedIds)}
                  disabled={saving}
                  sx={{ color: '#EF9A9A' }}
                >
                  Reject ({selectedPendingCount})
                </Button>
              </Box>
            }
          >
            {selectedPendingCount} item(s) selected for approval
          </Alert>
        )}

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#D4AF37' }} />
          </Box>
        ) : selections.length === 0 ? (
          <Paper sx={{ 
            p: 4, 
            textAlign: 'center', 
            bgcolor: 'rgb(10, 25, 41)', 
            border: '1px solid #D4AF37',
            borderRadius: 3
          }}>
            <EventAvailable sx={{ fontSize: 48, color: '#B4C4A8', mb: 2 }} />
            <Typography sx={{ color: '#B4C4A8' }}>No holiday selections found</Typography>
            <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
              Try changing your filters or check back later
            </Typography>
          </Paper>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#2A3E24' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.length === pendingCount && pendingCount > 0}
                        indeterminate={selectedIds.length > 0 && selectedIds.length < pendingCount}
                        onChange={handleSelectAll}
                        disabled={pendingCount === 0}
                        sx={{ color: '#D4AF37', '&.Mui-checked': { color: '#D4AF37' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Employee ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Holiday</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Day</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Requested Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selections.map((selection) => (
                    <TableRow 
                      key={selection.id}
                      hover
                      sx={{ 
                        '&:hover': { bgcolor: 'rgba(212,175,55,0.05)' },
                        bgcolor: selectedIds.includes(selection.id) ? 'rgba(212,175,55,0.1)' : 'inherit'
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(selection.id)}
                          onChange={() => handleSelect(selection.id)}
                          disabled={selection.status !== 'pending'}
                          sx={{ color: '#D4AF37', '&.Mui-checked': { color: '#D4AF37' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', fontSize: '0.875rem' }}>
                            {selection.employee?.first_name?.[0]}{selection.employee?.last_name?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#FFFFFF' }}>
                              {selection.employee?.first_name} {selection.employee?.last_name}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: '#B4C4A8' }}>{selection.employee?.employee_id || '-'}</TableCell>
                      <TableCell sx={{ color: '#B4C4A8' }}>{selection.employee?.department || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#FFFFFF' }}>
                          {selection.holiday?.holiday_name}
                        </Typography>
                        {selection.holiday?.description && (
                          <Typography variant="caption" sx={{ color: '#B4C4A8', display: 'block' }}>
                            {selection.holiday.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: '#B4C4A8' }}>
                        {selection.holiday?.holiday_date && 
                          new Date(selection.holiday.holiday_date).toLocaleDateString('en-IN')
                        }
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={selection.holiday?.day || '-'} 
                          size="small" 
                          variant="outlined"
                          sx={{ borderColor: '#D4AF37', color: '#D4AF37' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={selection.holiday?.type || 'public'} 
                          size="small"
                          sx={getHolidayTypeColor(selection.holiday?.type)}
                        />
                      </TableCell>
                      <TableCell>{getStatusChip(selection.status)}</TableCell>
                      <TableCell sx={{ color: '#B4C4A8' }}>
                        {selection.requested_date && 
                          new Date(selection.requested_date).toLocaleDateString('en-IN')
                        }
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(selection)}
                              sx={{ color: '#90CAF9', '&:hover': { bgcolor: 'rgba(144, 202, 249, 0.1)' } }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {selection.status === 'pending' && (
                            <>
                              <Tooltip title="Approve Holiday">
                                <IconButton
                                  size="small"
                                  onClick={() => handleApprove(selection.id)}
                                  disabled={saving}
                                  sx={{ color: '#81C784', '&:hover': { bgcolor: 'rgba(129, 199, 132, 0.1)' } }}
                                >
                                  <Check />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject Holiday">
                                <IconButton
                                  size="small"
                                  onClick={() => openRejectDialog(selection.id)}
                                  disabled={saving}
                                  sx={{ color: '#EF9A9A', '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.1)' } }}
                                >
                                  <Close />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page}
                  onChange={handlePageChange}
                  sx={{
                    '& .MuiPaginationItem-root': { color: '#D4AF37' },
                    '& .Mui-selected': { bgcolor: 'rgba(212,175,55,0.2)', color: '#D4AF37' }
                  }}
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}

        {/* Reject Dialog */}
        <Dialog 
          open={rejectDialog.open} 
          onClose={() => setRejectDialog({ open: false, ids: [], reason: '' })}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgb(10, 25, 41)',
              border: '2px solid #D4AF37',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: '#EF4444', 
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Warning />
            Reject Holiday Selection
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography sx={{ mb: 2, color: '#B4C4A8' }}>
              Are you sure you want to reject {rejectDialog.ids.length} holiday selection(s)?
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for rejection"
              placeholder="Please provide a reason for rejection..."
              value={rejectDialog.reason}
              onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
              helperText="This reason will be visible to the employee"
              InputLabelProps={{ sx: { color: '#B4C4A8' } }}
              InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              FormHelperTextProps={{ sx: { color: '#B4C4A8' } }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.3)' }}>
            <Button 
              onClick={() => setRejectDialog({ open: false, ids: [], reason: '' })}
              disabled={saving}
              sx={{ color: '#B4C4A8', '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleReject} 
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <Close />}
              sx={{ bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' } }}
            >
              {saving ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Details Dialog */}
        <Dialog
          open={viewDialog.open}
          onClose={() => setViewDialog({ open: false, selection: null })}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgb(10, 25, 41)',
              border: '2px solid #D4AF37',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
            Holiday Selection Details
          </DialogTitle>
          <DialogContent dividers sx={{ borderColor: 'rgba(212,175,55,0.3)' }}>
            {viewDialog.selection && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#D4AF37', mb: 1 }}>Employee Details</Typography>
                  <Box sx={{ p: 2, bgcolor: '#2A3E24', borderRadius: 2, mb: 2 }}>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                      {viewDialog.selection.employee?.first_name} {viewDialog.selection.employee?.last_name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                      ID: {viewDialog.selection.employee?.employee_id}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                      Department: {viewDialog.selection.employee?.department}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                      Email: {viewDialog.selection.employee?.email}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#D4AF37', mb: 1 }}>Holiday Details</Typography>
                  <Box sx={{ p: 2, bgcolor: '#2A3E24', borderRadius: 2, mb: 2 }}>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                      {viewDialog.selection.holiday?.holiday_name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                      Date: {viewDialog.selection.holiday?.holiday_date && 
                        new Date(viewDialog.selection.holiday.holiday_date).toLocaleDateString('en-IN')
                      }
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                      Day: {viewDialog.selection.holiday?.day}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                      Type: {viewDialog.selection.holiday?.type}
                    </Typography>
                    {viewDialog.selection.holiday?.description && (
                      <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                        Description: {viewDialog.selection.holiday.description}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ color: '#D4AF37', mb: 1 }}>Selection Details</Typography>
                  <Box sx={{ p: 2, bgcolor: '#2A3E24', borderRadius: 2 }}>
                    <Box sx={{ mb: 1 }}>{getStatusChip(viewDialog.selection.status)}</Box>
                    <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                      Requested Date: {viewDialog.selection.requested_date && 
                        new Date(viewDialog.selection.requested_date).toLocaleString('en-IN')
                      }
                    </Typography>
                    {viewDialog.selection.rejection_reason && (
                      <Typography variant="body2" sx={{ color: '#EF9A9A', mt: 1 }}>
                        Rejection Reason: {viewDialog.selection.rejection_reason}
                      </Typography>
                    )}
                    {viewDialog.selection.approved_date && (
                      <Typography variant="body2" sx={{ color: '#81C784', mt: 1 }}>
                        Approved Date: {new Date(viewDialog.selection.approved_date).toLocaleString('en-IN')}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.3)' }}>
            <Button 
              onClick={() => setViewDialog({ open: false, selection: null })}
              sx={{ color: '#B4C4A8', '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default HolidayApprovals;